import { useEffect, useState } from "react";
import { Rocket, Upload, ShieldCheck, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { RippBadge } from "./primitives";
import { RippReachRow } from "./RippReachRow";
import { RippDexKit } from "./RippDexKit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;
const btn = "rounded-sm border border-[var(--r2-border)] px-1.5 py-[2px] text-[8px] font-bold tracking-[0.14em] text-[var(--r2-text2)] hover:text-[var(--r2-text1)] disabled:opacity-40 flex items-center gap-1";
const err = (e) => toast(e.response?.data?.detail || e.message);

const FacePicker = ({ st, onPick, locked }) => (
  <div className="flex gap-1.5" data-testid="ripp-face-picker">
    {Object.entries(st.images || {}).map(([k, src]) => (
      <button key={k} disabled={locked} onClick={() => onPick(k)} data-testid={`ripp-face-${k}`}
        className={`rounded-full overflow-hidden border-2 transition-transform hover:scale-105 ${st.image === k ? "border-[var(--r2-amber)]" : "border-transparent opacity-60"}`}>
        <img src={`${BASE}${src}`} alt={k} className="h-14 w-14 object-cover" />
      </button>
    ))}
  </div>
);

const LiveCard = ({ st }) => (
  <div className="space-y-1" data-testid="ripp-launch-live">
  <div className="flex items-center gap-2 text-[8px] font-mono">
    <RippBadge tone="emerald" testid="ripp-launch-live-badge">$RIPP ON-CHAIN</RippBadge>
    <span className="truncate max-w-[200px] text-[var(--r2-text1)]" data-testid="ripp-launch-mint">{st.mint}</span>
    <a href={`https://pump.fun/coin/${st.mint}`} target="_blank" rel="noreferrer" className="text-[var(--r2-cyan)] hover:underline flex items-center gap-0.5" data-testid="ripp-launch-pump-link">pump.fun <ExternalLink size={9} /></a>
    <a href={`https://solscan.io/tx/${st.signature}`} target="_blank" rel="noreferrer" className="text-[var(--r2-text2)] hover:underline" data-testid="ripp-launch-tx-link">tx</a>
    {st.genesis_scheduled && <span className="text-[var(--r2-text2)]" data-testid="ripp-genesis-scheduled">Genesis mint opens {st.genesis_scheduled.replace("T", " ").slice(0, 16)} UTC</span>}
  </div>
  <RippDexKit kit={st.dexscreener} mint={st.mint} />
  </div>
);

export const RippLaunchPanel = () => {
  const [st, setSt] = useState(null);
  const [check, setCheck] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState("");
  const [ready, setReady] = useState(false);
  const load = () => axios.get(`${API}/ripp-token`).then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st) return null;
  if (st.mint) return <LiveCard st={st} />;
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { err(e); } setBusy(""); };
  const pick = (image) => run("pick", async () => { setSt((await axios.post(`${API}/ripp-token/config`, { patch: { image } })).data && { ...st, image, metadata_uri: null }); setCheck(null); });
  const prepare = () => run("prepare", async () => { await axios.post(`${API}/ripp-token/prepare`); await load(); toast("Face + metadata stored permanently"); });
  const dry = () => run("dry", async () => setCheck((await axios.post(`${API}/ripp-token/launch`, { dry_run: true })).data));
  const fire = () => run("fire", async () => { const r = await axios.post(`${API}/ripp-token/launch`, { confirm }); toast(`$RIPP launched: ${r.data.mint}`); await load(); });
  const short = check && check.balance_sol < check.need_sol;
  return (
    <div className="rounded-sm border border-[var(--r2-amber)]/60 bg-black/30 p-2 space-y-1.5 font-mono" data-testid="ripp-launch-panel">
      <div className="flex items-center gap-1.5 text-[8px] font-bold tracking-[0.14em] text-[var(--r2-amber)]"><Rocket size={10} /> $RIPP · PUMP.FUN FAIR LAUNCH · first buy {st.dev_buy_sol} SOL from the trading wallet</div>
      <div className="flex items-center gap-2">
        <FacePicker st={st} onPick={pick} locked={!!busy} />
        <div className="text-[8px] leading-snug text-[var(--r2-text2)] flex-1" data-testid="ripp-launch-meta">
          <div className="text-[var(--r2-text1)] font-bold">{st.name} · ${st.symbol}</div>
          <div className="line-clamp-2" title={st.description}>{st.description}</div>
          <div>{st.website} · {st.twitter}</div>
        </div>
      </div>
      <RippReachRow onReady={setReady} />
      <div className="flex flex-wrap items-center gap-1.5">
        <button onClick={prepare} disabled={!!busy} className={btn} data-testid="ripp-launch-prepare"><Upload size={10} /> {busy === "prepare" ? "UPLOADING…" : st.metadata_uri ? "1 · STORED ✓" : "1 · STORE FACE"}</button>
        <button onClick={dry} disabled={!!busy || !st.metadata_uri} className={btn} data-testid="ripp-launch-check"><ShieldCheck size={10} /> {busy === "dry" ? "CHECKING…" : "2 · CHECK LAUNCH"}</button>
        {check && !short && !check.sim_err && <>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="type LAUNCH $RIPP" data-testid="ripp-launch-confirm" className="w-32 rounded-sm border border-[var(--r2-border)] bg-transparent px-1.5 py-[2px] text-[9px] text-[var(--r2-text1)] focus:border-[var(--r2-red)] focus:outline-none" />
          <button onClick={fire} disabled={!!busy || !ready || confirm !== "LAUNCH $RIPP"} data-testid="ripp-launch-fire" className="flex items-center gap-1 rounded-sm bg-[var(--r2-amber)] px-2 py-[3px] text-[8px] font-black tracking-[0.14em] text-black disabled:opacity-40"><Rocket size={10} /> {busy === "fire" ? "LAUNCHING…" : "3 · LAUNCH $RIPP"}</button>
        </>}
      </div>
      {check && <div className={`text-[8px] ${short || check.sim_err ? "text-[var(--r2-red)]" : "text-[var(--r2-emerald)]"}`} data-testid="ripp-launch-check-result">
        wallet {check.balance_sol} SOL · needs {check.need_sol} SOL · {short ? "add SOL first" : check.sim_err ? `simulation failed: ${JSON.stringify(check.sim_err)}` : "simulation passed — ready"}</div>}
      {st.last_error && <div className="text-[8px] text-[var(--r2-red)]" data-testid="ripp-launch-error">last error: {st.last_error}</div>}
    </div>
  );
};
